import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, ChevronRight, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import StatusPill from "@/components/StatusPill";

const fmt = (iso) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
const dateLine = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

const isToday = (iso) => {
  const d = new Date(iso);
  const t = new Date();
  return d.toDateString() === t.toDateString();
};
const isPast = (iso) => new Date(iso) < new Date(new Date().setHours(0, 0, 0, 0));

const OPEN_STATUSES = ["NEW", "SCHEDULED", "EN_ROUTE", "ON_SITE"];

function JobCard({ job }) {
  return (
    <Link
      to={`/tech/jobs/${job.id}`}
      data-testid={`tech-job-${job.id}`}
      className={`block bg-white rounded-2xl border border-[#E4E6EF] p-4 shadow-sm active:scale-[0.99] transition-transform duration-200 status-border-${job.status}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-[#14162E] leading-tight">{job.title}</h3>
          <p className="text-sm text-[#6A6E85] mt-1">{job.client_name}</p>
          <p className="text-sm text-[#6A6E85] flex items-center gap-1 mt-1">
            <MapPin size={13} /> {job.site_address}
          </p>
          <p className="text-sm text-[#181854] font-semibold flex items-center gap-1 mt-2">
            <Clock size={13} /> {isToday(job.scheduled_at) ? fmt(job.scheduled_at) : `${dateLine(job.scheduled_at)} ${fmt(job.scheduled_at)}`}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusPill status={job.status} />
          <ChevronRight size={20} className="text-[#6A6E85]" />
        </div>
      </div>
    </Link>
  );
}

export default function TechMyJobs() {
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data));
  }, []);

  const open = jobs.filter((j) => OPEN_STATUSES.includes(j.status));
  const overdue = open.filter((j) => !isToday(j.scheduled_at) && isPast(j.scheduled_at));
  const today = open.filter((j) => isToday(j.scheduled_at));
  const upcoming = open.filter(
    (j) => !isToday(j.scheduled_at) && !isPast(j.scheduled_at)
  );

  return (
    <div className="p-4 space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-[#6A6E85] font-semibold">
          {dateLine(new Date().toISOString())}
        </p>
        <h1 className="text-2xl font-bold text-[#181854] tracking-tight mt-0.5">My jobs today</h1>
        <p className="text-[#6A6E85] text-sm mt-1">
          {today.length === 0
            ? overdue.length > 0
              ? `${overdue.length} overdue to catch up on.`
              : "You're all clear today."
            : `${today.length} to knock out.`}
        </p>
      </div>

      {overdue.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-[#B45309] font-semibold flex items-center gap-1.5 mb-2">
            <AlertTriangle size={13} /> Overdue ({overdue.length})
          </h2>
          <div className="space-y-3">
            {overdue.map((j) => (
              <JobCard key={j.id} job={j} />
            ))}
          </div>
        </section>
      )}

      <section className="space-y-3">
        {today.length > 0 && (
          <h2 className="text-xs uppercase tracking-widest text-[#6A6E85] font-semibold">Today</h2>
        )}
        {today.map((j) => (
          <JobCard key={j.id} job={j} />
        ))}
        {today.length === 0 && overdue.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#E4E6EF] p-8 text-center">
            <p className="text-[#6A6E85]">No jobs scheduled for today.</p>
          </div>
        )}
      </section>

      {upcoming.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-[#6A6E85] font-semibold mb-2">Coming up</h2>
          <div className="space-y-2">
            {upcoming.map((j) => (
              <Link
                key={j.id}
                to={`/tech/jobs/${j.id}`}
                data-testid={`tech-job-${j.id}`}
                className="block bg-[#ECEDF6] rounded-xl p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#14162E] truncate">{j.title}</p>
                    <p className="text-xs text-[#6A6E85] truncate">
                      {j.client_name} · {dateLine(j.scheduled_at)} {fmt(j.scheduled_at)}
                    </p>
                  </div>
                  <StatusPill status={j.status} />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
